import { createSelector, weakMapMemoize } from '@reduxjs/toolkit';

import { BalanceType } from '../../../api/fetchers/userInfo/types';
import { countTotalBet, getLastBetType, getMinimalBet } from '../../../utils/bets';
import { computeBalance } from '../../compositeSelectors/helpers/computeBalance';
import type { CoreRootState } from '../../CoreStore';
import { useCoreSelector } from '../../CoreStore';
import { selectFreeChipBalance } from '../bonusPromotions/selectors';
import { ChipTypes } from '../chips/types';
import {
  selectCurrencyDecimals,
  selectDeductibleBalance,
  selectIsBetsOpen,
  selectIsLoadedInActiveRound,
  selectUserBalance,
  selectWinResult,
} from '../game/selectors';

import type { AcceptedBet, BetsSliceType, BetType, DragBufferType, IBetLimits, IBetSpotValue, ILimit } from './types';

export const selectBets = (state: CoreRootState): BetType | undefined =>
  state.bets.betStack?.[state.bets.betStack.length - 1];
export const selectLastBet = (state: CoreRootState): BetType | null => state.bets.lastBet;
const selectBetsReady = (state: CoreRootState): boolean => Boolean(state.bets.betList);
const selectBetList = (state: CoreRootState): number[] | null => state.bets.betList;
const selectBetLimits = (state: CoreRootState): IBetLimits | null => state.bets.betLimits;
const selectMinBet = (state: CoreRootState): number => getMinimalBet(state.bets.betLimits);
const selectRawTotalBet = (state: CoreRootState): number => state.bets.rawTotalBet;
const selectDisabledBetSpots = (state: CoreRootState): string[] => state.bets.disabledBetSpots;
export const selectTotalBet = (state: CoreRootState): number => state.bets.totalBet;
export const selectHasTotalBet = (state: CoreRootState): boolean => state.bets.totalBet > 0;
export const selectAcceptedBets = (state: CoreRootState): AcceptedBet[] => state.bets.acceptedBets;
const selectDragBuffer = (state: CoreRootState): DragBufferType | null =>
  state.bets.dragBuffer?.preliminary ? null : state.bets.dragBuffer;
const selectPreliminaryDragBuffer = (state: CoreRootState): DragBufferType | null => state.bets.dragBuffer;
const selectIsDragging = (state: CoreRootState): boolean => Boolean(state.bets.dragBuffer);
const selectUpdateTimestamp = (state: CoreRootState): number => state.bets.updateTimestamp;
const selectTableLimits = (state: CoreRootState): ILimit | null => state.bets.tableLimits;
export const selectPlacedBetType = (state: CoreRootState): string => state.bets.placedBetType;

export const selectCompositeBalance = createSelector(
  [
    selectUserBalance,
    selectCurrencyDecimals,
    selectUpdateTimestamp,
    selectIsBetsOpen,
    selectIsLoadedInActiveRound,
    selectTotalBet,
    selectWinResult,
    selectPlacedBetType,
  ],
  (balance, decimals, betTimestamp, isBetsOpenStatus, isLoadedInActiveRound, totalBet, winResult, placedBetType) => {
    if (!balance) return;

    const balanceTimestamp = balance.updateTimestamp;
    const winResultTimestamp = winResult?.updateTimestamp ?? 0;
    /**
     * isLoadedInActiveRound - the server is sending in balance with the deducted bet in case of restore, so no need to do it manually as we do it otherwise.
     * isBetsOpenStatus - extra check for game state that the bets are actually closed and balance response from the server already should have the correct balance in response.
     * winResult - if we get in state where we have a win result, but the balance request has completed sooner and already should have the correct balance in response.
     */
    if (
      isLoadedInActiveRound ||
      (!isBetsOpenStatus && betTimestamp < balanceTimestamp) ||
      (winResult && winResultTimestamp < balanceTimestamp)
    ) {
      return balance;
    }

    return computeBalance(
      balance,
      totalBet,
      decimals,
      placedBetType === ChipTypes.FreeChip,
      /**
       * If the win result is the freshest of actions that affect balance - add it to the new balance amount (without subtracting totalBet).
       */
      winResult && winResultTimestamp > balanceTimestamp ? winResult.amount : undefined
    );
  },
  { argsMemoize: weakMapMemoize, memoize: weakMapMemoize }
);

const selectMultiplierFactor = (state: CoreRootState): number | null => state.bets.multiplierFactor;
export const selectIsLowCompositeBalance = createSelector(
  selectCompositeBalance,
  selectFreeChipBalance,
  selectMinBet,
  (balance, freeChipBalance, minBet) => {
    const totalBalance = (balance?.amount ?? 0) + (balance?.bonus ?? 0);
    return freeChipBalance < minBet && totalBalance < minBet;
  }
);

export const selectReBetEnabled = createSelector(
  [
    selectDeductibleBalance,
    selectCurrencyDecimals,
    selectLastBet,
    selectMultiplierFactor,
    selectDisabledBetSpots,
    selectFreeChipBalance,
  ],
  (balance, digits, lastBet, multiplierFactor, disabledBetSpots, freeChipBalance) => {
    // Conditions
    // 1) there is a last bet
    // 2) It is not 0
    // 3) It does not include bet spots that are disabled now
    // 4) It fits in balance
    if (lastBet) {
      const savedBet = lastBet.betSpots;
      const betUnavailable = Object.keys(savedBet || {}).some((el) => {
        const value = savedBet?.[el]?.value ?? 0;
        return value > 0 && disabledBetSpots.includes(el);
      });
      let totalBet = countTotalBet(savedBet, digits);
      if (!betUnavailable) {
        const lastBetType = getLastBetType(lastBet);
        if (lastBetType === ChipTypes.FreeChip) {
          balance = freeChipBalance;
        } else if (multiplierFactor) {
          totalBet *= multiplierFactor;
        }
        return totalBet <= balance;
      }
    }
    return false;
  }
);

export const selectIsLowBalance = createSelector(
  [selectDeductibleBalance, selectFreeChipBalance, selectMinBet],
  (balance, freeChipBalance, minBet) => {
    return freeChipBalance < minBet && balance < minBet;
  }
);

export const selectInvalidBetspots = createSelector(
  [
    (state: CoreRootState): BetType | undefined => state.bets.betStack?.[state.bets.betStack.length - 1],
    (state: CoreRootState): IBetLimits | null => state.bets.betLimits,
  ],
  (placedBets, betLimits) => {
    if (placedBets && betLimits) {
      const { betSpots } = placedBets;
      return Object.keys(betSpots).filter((key) => {
        const betSpot = betSpots[key];
        const betLimit = betLimits[key];
        return betLimit && betSpot && betSpot.value > 0 && betSpot.value < betLimit.min;
      });
    }
    return [];
  }
);

export const useBetSlice = {
  bets: (): BetType | undefined => {
    return useCoreSelector(selectBets);
  },
  lastBet: (): BetsSliceType['lastBet'] => {
    return useCoreSelector(selectLastBet);
  },
  betsReady: (): boolean => {
    return useCoreSelector(selectBetsReady);
  },
  betList: (): BetsSliceType['betList'] => {
    return useCoreSelector(selectBetList);
  },
  betLimits: (): BetsSliceType['betLimits'] => {
    return useCoreSelector(selectBetLimits);
  },
  getMinBet: (): number => {
    return useCoreSelector(selectMinBet);
  },
  rawTotalBet: (): number => {
    return useCoreSelector(selectRawTotalBet);
  },
  getDisabledBetSpots: (): string[] => {
    return useCoreSelector(selectDisabledBetSpots);
  },
  totalBet: (): number => {
    return useCoreSelector(selectTotalBet);
  },
  hasTotalBet: (): boolean => {
    return useCoreSelector(selectHasTotalBet);
  },
  acceptedBets: (): BetsSliceType['acceptedBets'] => {
    return useCoreSelector(selectAcceptedBets);
  },
  /**
   * @returns string[] - Array of bet spots id's that are invalid based on min bet limits
   */
  invalidBetspots: (): string[] => {
    return useCoreSelector(selectInvalidBetspots);
  },
  hasInvalidBets: (): boolean => {
    return useCoreSelector((state) => selectInvalidBetspots(state).length > 0);
  },
  getDragBuffer: (): DragBufferType | null => {
    return useCoreSelector(selectDragBuffer);
  },
  getPreliminaryDragBuffer: (): DragBufferType | null => {
    return useCoreSelector(selectPreliminaryDragBuffer);
  },
  getIsDragging: (): boolean => {
    return useCoreSelector(selectIsDragging);
  },
  /**
   * Get timestamp of when was the last time betStack was updated
   */
  getUpdateTimestamp: (): number => {
    return useCoreSelector(selectUpdateTimestamp);
  },
  getTableLimits: (): ILimit | null => {
    return useCoreSelector(selectTableLimits);
  },
  getPlacedBetType: (): string => {
    return useCoreSelector(selectPlacedBetType);
  },
  getMultiplierFactor: (): number | null => {
    return useCoreSelector(selectMultiplierFactor);
  },
  isLowBalance: (): boolean => {
    return useCoreSelector(selectIsLowBalance);
  },
  isLowCompositeBalance: (): boolean => {
    return useCoreSelector(selectIsLowCompositeBalance);
  },
  isReBetEnabled: (): boolean => {
    return useCoreSelector(selectReBetEnabled);
  },
  getBetSpot: (betSpot: number): IBetSpotValue | undefined => {
    return useCoreSelector((state) => state.bets.betStack?.[state.bets.betStack.length - 1]?.betSpots[betSpot]);
  },
  getCompositeBalance: (): BalanceType | undefined => {
    return useCoreSelector(selectCompositeBalance);
  },
  getRebetAttempts: (): number => {
    return useCoreSelector((state) => state.bets.rebetAttempts);
  },
};
